import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Button, Card, Empty, Select, Space, Spin, Typography, message, Tag } from 'antd';
import { ReloadOutlined, LeftOutlined, RightOutlined, CalendarOutlined, InboxOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { getUserReservations, getUsers } from '../services/api';

const { Title, Text } = Typography;

const stateColorMap = {
  待签到: 'warning',
  已关闭: 'default',
  进行中: 'success',
  已完成: 'success',
};

const stateBgColorMap = {
  待签到: 'rgba(245, 158, 11, 0.08)',
  已关闭: 'rgba(148, 163, 184, 0.08)',
  进行中: 'rgba(16, 185, 129, 0.08)',
  已完成: 'rgba(16, 185, 129, 0.08)',
};

const stateBorderColorMap = {
  待签到: 'rgba(245, 158, 11, 0.3)',
  已关闭: 'rgba(148, 163, 184, 0.3)',
  进行中: 'rgba(16, 185, 129, 0.3)',
  已完成: 'rgba(16, 185, 129, 0.3)',
};

const weekDayNames = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

const MobileResCard = ({ dateKey, date, items, today }) => {
  const [expanded, setExpanded] = useState(false);
  const reservedCount = items.filter(i => i.state === '待签到' || i.state === '进行中').length;
  return (
    <div key={dateKey} className={`reservation-mobile-card${today ? ' is-today' : ''}`}>
      <div className="reservation-mobile-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="reservation-mobile-date">
          <span className="reservation-mobile-weekday">{weekDayNames[date.day()]}</span>
          <span className="reservation-mobile-day">{date.format('MM/DD')}</span>
          {today && <span className="reservation-mobile-today-badge">今天</span>}
        </div>
        <Space size={8}>
          <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>{reservedCount}/{items.length}</Tag>
          {expanded ? <UpOutlined style={{ fontSize: 10, color: 'var(--text-slate-400)' }} /> : <DownOutlined style={{ fontSize: 10, color: 'var(--text-slate-400)' }} />}
        </Space>
      </div>
      {expanded && (
        <div className="reservation-mobile-items">
          {items.map((item, index) => (
            <div
              key={`${dateKey}-${item.time}-${index}`}
              className="reservation-mobile-item"
              style={{
                background: stateBgColorMap[item.state] || '#f5f5f5',
                borderColor: stateBorderColorMap[item.state] || '#d9d9d9',
              }}
            >
              <div className="reservation-mobile-item-main">
                <span className="reservation-mobile-item-time">{item.time}</span>
                <span className="reservation-mobile-item-place">
                  {item.place_name || item.resource_name || `场地${item.resource_id}`}
                </span>
              </div>
              <Tag size="small" color={stateColorMap[item.state] || 'default'}>
                {item.state || '已预约'}
              </Tag>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const UserReservationsList = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserUid, setSelectedUserUid] = useState(undefined);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [reservationByDate, setReservationByDate] = useState({});
  const [orderCount, setOrderCount] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(dayjs());

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await getUsers();
        if (response.status === 'success') {
          setUsers(response.data || []);
        } else {
          message.error(response.message || '获取用户列表失败');
        }
      } catch (error) {
        console.error('获取用户列表失败:', error);
        message.error('获取用户列表失败，请稍后重试');
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  const handleQueryReservations = useCallback(async (uid) => {
    const queryUid = uid || selectedUserUid;
    if (!queryUid) {
      message.warning('请先选择用户');
      return;
    }

    setLoadingReservations(true);
    try {
      const response = await getUserReservations(queryUid);
      if (response.status === 'success') {
        const matchedUser = users.find((item) => item.uid === queryUid) || response.user || null;
        const reservedTimes = response.data?.reserved_times || [];
        const groupedReservations = response.data?.reservation_by_date || {};

        setSelectedUser(matchedUser);
        setReservations(reservedTimes);
        setReservationByDate(groupedReservations);
        setOrderCount((response.data?.orders || []).length);

        if (reservedTimes.length > 0 && reservedTimes[0].date) {
          setCurrentWeek(dayjs(reservedTimes[0].date));
        }
      } else {
        message.error(response.message || '获取已预约列表失败');
      }
    } catch (error) {
      console.error('获取已预约列表失败:', error);
      message.error('获取已预约列表失败，请稍后重试');
    } finally {
      setLoadingReservations(false);
    }
  }, [selectedUserUid, users]);

  useEffect(() => {
    if (selectedUserUid) {
      handleQueryReservations(selectedUserUid);
    } else {
      setSelectedUser(null);
      setReservations([]);
      setReservationByDate({});
      setOrderCount(0);
    }
  }, [selectedUserUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const weekDates = useMemo(() => {
    const startOfWeek = currentWeek.startOf('week');
    return Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day'));
  }, [currentWeek]);

  const prevWeek = () => setCurrentWeek(currentWeek.subtract(1, 'week'));
  const nextWeek = () => setCurrentWeek(currentWeek.add(1, 'week'));
  const goToToday = () => setCurrentWeek(dayjs());
  const isToday = (date) => date.isSame(dayjs(), 'day');

  const renderWeekView = () => (
    <div className="reservation-week-view">
      <div className="reservation-week-header">
        <div className="reservation-week-nav">
          <Button icon={<LeftOutlined />} onClick={prevWeek} size="small" />
          <span className="reservation-week-title">
            {currentWeek.format('YYYY年MM月')} 第{currentWeek.week()}周
          </span>
          <Button icon={<RightOutlined />} onClick={nextWeek} size="small" />
        </div>
        <Button type="link" onClick={goToToday} icon={<CalendarOutlined />}>
          回到本周
        </Button>
      </div>
      
      <div className="reservation-week-grid">
        {weekDates.map((date) => {
          const dateKey = date.format('YYYY-MM-DD');
          const items = reservationByDate[dateKey] || [];
          const today = isToday(date);
          
          return (
            <div key={dateKey} className={`reservation-week-day${today ? ' is-today' : ''}`}>
              <div className="reservation-week-day-header">
                <span className="reservation-week-day-name">{weekDayNames[date.day()]}</span>
                <span className="reservation-week-day-date">{date.format('MM/DD')}</span>
                {today && <span className="reservation-week-today-badge">今天</span>}
              </div>
              <div className="reservation-week-day-content">
                {items.length > 0 ? (
                  items.map((item, index) => (
                    <div
                      key={`${dateKey}-${item.time}-${index}`}
                      className="reservation-week-item"
                      style={{
                        background: stateBgColorMap[item.state] || '#f5f5f5',
                        borderColor: stateBorderColorMap[item.state] || '#d9d9d9',
                      }}
                    >
                      <div className="reservation-week-item-time">{item.time}</div>
                      <div className="reservation-week-item-place">{item.place_name || item.resource_name || `场地${item.resource_id}`}</div>
                      <Tag size="small" color={stateColorMap[item.state] || 'default'} style={{ fontSize: '11px' }}>
                        {item.state || '已预约'}
                      </Tag>
                    </div>
                  ))
                ) : (
                  <div className="reservation-week-empty">无预约</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderMobileView = () => (
    <div className="reservation-mobile-view">
      <div className="reservation-mobile-header">
        <Button icon={<LeftOutlined />} onClick={prevWeek} shape="circle" size="small" />
        <span className="reservation-mobile-title">
          {currentWeek.format('YYYY年MM月')} 第{currentWeek.week()}周
        </span>
        <Button icon={<RightOutlined />} onClick={nextWeek} shape="circle" size="small" />
      </div>
      
      <Button type="link" onClick={goToToday} size="small" style={{ marginBottom: 12 }}>
        回到本周
      </Button>
      
      <div className="reservation-mobile-list">
        {weekDates.map((date) => {
          const dateKey = date.format('YYYY-MM-DD');
          const items = reservationByDate[dateKey] || [];
          const today = isToday(date);
          
          if (items.length === 0) return null;
          
          return (
            <MobileResCard
              key={dateKey}
              dateKey={dateKey}
              date={date}
              items={items}
              today={today}
            />
          );
        })}
        
        {weekDates.filter(date => (reservationByDate[date.format('YYYY-MM-DD')] || []).length > 0).length === 0 && (
          <div className="reservation-mobile-empty">
            <div className="reservation-mobile-empty-icon"><InboxOutlined style={{ fontSize: 48, color: 'var(--text-slate-400)' }} /></div>
            <div className="reservation-mobile-empty-text">本周暂无预约</div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>已预约列表</Title>
          <Text type="secondary">手动选择用户后查询该账号在 JoyReserve 上的已预约订单。</Text>
        </div>
        <Space wrap>
          <Select
            showSearch
            optionFilterProp="label"
            aria-label="预约用户"
            placeholder="搜索并选择用户"
            style={{ width: 240 }}
            loading={loadingUsers}
            value={selectedUserUid}
            onChange={setSelectedUserUid}
            options={users.map((user) => ({
              value: user.uid,
              label: `${user.name}${user.phone ? ` (${user.phone})` : ''}${user.status === 'disabled' ? ' [不可用]' : ''}`,
              disabled: user.status === 'disabled',
            }))}
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => handleQueryReservations()}
            loading={loadingReservations}
            disabled={!selectedUserUid}
          >
            刷新
          </Button>
        </Space>
      </div>

      {selectedUser && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message={`当前用户：${selectedUser.name}`}
          description={`共查询到 ${orderCount} 个订单，展开后共 ${reservations.length} 条预约时段。`}
        />
      )}

      <Spin spinning={loadingReservations}>
        {selectedUser ? (
          reservations.length > 0 ? (
            <>
              <div className="reservation-desktop-view">
                {renderWeekView()}
              </div>
              <div className="reservation-mobile-only">
                {renderMobileView()}
              </div>
            </>
          ) : (
            <Empty description="该用户暂无已预约记录" />
          )
        ) : (
          <Empty description="请选择用户后查询" />
        )}
      </Spin>
    </Card>
  );
};

export default UserReservationsList;